import { defaultLocale, supportedLocales, type SupportedLocale } from "@blizhub/shared";

const adminSegment = "admin";
const marketplaceSegment = "markedsplads";
const messagesSegment = "beskeder";
const foreningerSegment = "foreninger";
const advisorAccessSegment = "radgiveradgang";
const documentsSegment = "dokumenter";
const faqSegment = "faq";
const userActivitySegment = "brugeraktivitet";

export function isSupportedRouteLocale(value: string | null | undefined): value is SupportedLocale {
  return supportedLocales.includes(value as SupportedLocale);
}

export function getPathLocale(pathname: string | null | undefined): SupportedLocale | null {
  const firstSegment = pathname?.split("/").find(Boolean) ?? null;

  return isSupportedRouteLocale(firstSegment) ? firstSegment : null;
}

export function getPreferredRouteLocale(pathname: string | null | undefined, fallback: SupportedLocale = defaultLocale): SupportedLocale {
  return getPathLocale(pathname) ?? fallback;
}

export function stripLocalePrefix(pathname: string | null | undefined): string {
  const normalizedPathname = pathname || "/";
  const segments = normalizedPathname.split("/").filter(Boolean);

  if (isSupportedRouteLocale(segments[0])) {
    const stripped = segments.slice(1).join("/");

    return stripped ? `/${stripped}` : "/";
  }

  return normalizedPathname;
}

export function createLocalizedAdminOverviewHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}`;
}

export function createLocalizedAdminAccountHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/account`;
}

export function createLocalizedAdminChatHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/chat`;
}

export function createLocalizedAdminMarketplaceHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/${marketplaceSegment}`;
}

export function createLocalizedAdminChatNewHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/chat/new`;
}

export function createLocalizedAdminChatThreadHref(locale: SupportedLocale, threadId: string): string {
  return `/${locale}/${adminSegment}/chat/${encodeURIComponent(threadId)}`;
}

export function createCustomerMessagesHref(): string {
  return `/${messagesSegment}`;
}

export function createCustomerMessagesThreadHref(threadId: string): string {
  return `/${messagesSegment}/${encodeURIComponent(threadId)}`;
}

export function createLocalizedCustomerMessagesHref(locale: SupportedLocale): string {
  return `/${locale}/${messagesSegment}`;
}

export function createLocalizedCustomerMessagesThreadHref(locale: SupportedLocale, threadId: string): string {
  return `/${locale}/${messagesSegment}/${encodeURIComponent(threadId)}`;
}

export function createCustomerMarketplaceHref(): string {
  return `/${marketplaceSegment}`;
}

export function createLocalizedCustomerMarketplaceHref(locale: SupportedLocale): string {
  return `/${locale}/${marketplaceSegment}`;
}

export function createLocalizedCustomerDocumentsHref(locale: SupportedLocale): string {
  return `/${locale}/${documentsSegment}`;
}

export function createLocalizedAdminDocumentsHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/${documentsSegment}`;
}

export function createLocalizedCustomerFaqHref(locale: SupportedLocale): string {
  return `/${locale}/${faqSegment}`;
}

export function createLocalizedAdminUserActivityHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/${userActivitySegment}`;
}

export function createLocalizedAdminFaqHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/${faqSegment}`;
}

export function createLocalizedAdminArchivedFaqHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/${faqSegment}/arkiverede`;
}

export function createLocalizedAdvisorAccessHref(locale: SupportedLocale): string {
  return `/${locale}/${advisorAccessSegment}`;
}

export function createLocalizedAdminFilesHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/files`;
}

export function createLocalizedAdminFilesBrowseHref(locale: SupportedLocale, connectionId: string): string {
  return `/${locale}/${adminSegment}/files/${encodeURIComponent(connectionId)}/browse`;
}

export function createLocalizedAdminSettingsHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/settings`;
}

export function createLocalizedAdminSmsEmailSettingsHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/settings/sms-email`;
}

export function createLocalizedAdminAppearanceSettingsHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/settings/appearance`;
}

export function createLocalizedAdminInvestorPortalSettingsHref(locale: SupportedLocale): string {
  return `/${locale}/${adminSegment}/settings/investorportal`;
}

export function createLocalizedAdminForeningHref(locale: SupportedLocale, associationId: string): string {
  return `/${locale}/${adminSegment}/${foreningerSegment}/${encodeURIComponent(associationId)}`;
}

export function createLocalizedAdminForeningArchivedFaqHref(locale: SupportedLocale, associationId: string): string {
  return `${createLocalizedAdminForeningHref(locale, associationId)}/faq/arkiverede`;
}

export function createLocalizedAdminForeningFaqHref(locale: SupportedLocale, associationId: string): string {
  return `${createLocalizedAdminForeningHref(locale, associationId)}?tab=faq`;
}

export function createLocalizedAdminForeningFilesTabHref(locale: SupportedLocale, associationId: string): string {
  return `${createLocalizedAdminForeningHref(locale, associationId)}?tab=files`;
}